/**
 * O dia: o que o job da madrugada encontrou, e se ele rodou.
 *
 * ── o elemento mais importante desta tela é uma ausência ──────────────────
 *
 * "última execução há N horas" responde **o job rodou?**, e essa é a única
 * pergunta que o próprio job não consegue responder: um processo morto não
 * reporta a própria morte. Todo o resto da tela mostra o que aconteceu; só
 * este campo mostra o que NÃO aconteceu.
 *
 * É por isso que ele fica no topo, em vermelho quando passa do limiar, e é por
 * isso que "nunca rodou" conta como atraso — um sistema recém-instalado que
 * dissesse "tudo em ordem" seria o pior jeito possível de descobrir que
 * ninguém agendou nada.
 *
 * ── por que a fila vem do banco e não do resumo da execução ───────────────
 *
 * O `resumo` gravado no `job_run` é a fotografia da madrugada. A fila anda
 * quando alguém decide, e mostrar o número congelado faria a tela contradizer
 * a lista de Oportunidades enquanto a pessoa trabalha nela.
 *
 * ── enquanto roda ─────────────────────────────────────────────────────────
 *
 * A rodada leva de dez a trinta minutos. A tela consulta a cada 5 s só
 * enquanto há execução em curso — poll permanente gastaria conexão o dia
 * inteiro para observar algo que muda uma vez por dia.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  api,
  ErroApi,
  type AchadoDaAuditoria,
  type EtapaDoJob,
  type TipoDeAchado,
} from "../api/cliente";
import { Etiquetas, Nota } from "../componentes/Selos";
import { Aviso, Cifras, ItemDeLista, Lista, Pilula, Secao, Vazio } from "../ds";
import { dia } from "../formato";
import css from "./Hoje.module.css";

//: Quantas oportunidades recentes a tela lista. Vinte cabem sem rolagem longa
//: e sobrevivem a um dia sem coleta — a JANELA (24 h) conta nos números acima,
//: mas a lista não se esvazia quando o job não roda: "nada novo hoje" e "o job
//: parou" não podem parecer a mesma coisa.
const RECENTES = 20;

/** AS OPORTUNIDADES MAIS RECENTES, que é o que esta tela mostra desde
 * 2026-09-22. Sai de `GET /api/licitacoes` com a ordem `recente`, e não de um
 * SELECT próprio: assim a lista herda o funil e a Regra 1 de `acervo._onde`,
 * como toda lista do sistema, e a ordem é a mesma que Oportunidades usa em
 * "Vistas por último".
 *
 * `faixa` pede as abas classificadas. Sem ela viriam também as que a IA
 * ainda não julgou, que não têm classificação para mostrar — elas aparecem
 * como número ("aguardando score") logo acima.
 *
 * `aguardando` entra junto (D17, 2026-10-05): com o portão da leitura ligado,
 * a recém-encontrada que teria score de Avaliar fica em "Aguardando leitura",
 * e sem ela aqui as melhores do dia sumiriam desta lista até o edital ser
 * lido. Com o portão desligado ninguém tem essa faixa, e nada muda. */
function Recentes() {
  const q = useQuery({
    queryKey: ["recentes-hoje"],
    // SEM AS REPROVADAS (pedido da gestao, 2026-09-28): a reprovada so aparece
    // na aba dela, e nao volta aqui quando a varredura a ve de novo.
    queryFn: () =>
      api.listar(
        {
          ordem: "recente",
          faixa: ["avaliar", "aguardando", "passar"],
          decisao: ["pendente", "aprovada"],
        },
        0,
        RECENTES,
      ),
  });

  return (
    <section className={css.recentes}>
      <h2 className={css.titulo}>Encontradas mais recentemente</h2>
      {q.isPending ? (
        <p className={css.espera}>Carregando…</p>
      ) : q.error ? (
        <p className={css.erro}>
          {q.error instanceof Error ? q.error.message : "não deu para listar"}
        </p>
      ) : !q.data?.itens.length ? (
        <p className={css.vazio}>
          Nenhuma oportunidade classificada ainda. Assim que a coleta rodar e a
          IA julgar, elas aparecem aqui.
        </p>
      ) : (
        <ul className={css.lista}>
          {q.data.itens.map((i) => (
            <li key={i.id}>
              <Link to={`/licitacao/${i.id}`}>
                <div className={css.cabeca}>
                  <Nota
                    valor={i.score}
                    faixa={i.faixa}
                    decisao={i.decisao}
                    recomendacao={i.recomendacao}
                    motivo={i.motivo_aba}
                  />
                  <b>{i.orgao ?? "Órgão não informado"}</b>
                  <span className={css.classificacao}>{i.classificacao ?? "—"}</span>
                  <Etiquetas itens={i.etiquetas} />
                </div>
                {/* A ORDEM É A DO §5 DO DOCUMENTO DE REGRAS: UF/Cidade,
                    objeto, valor, data, modalidade. Tipo de disputa e
                    sistema só existem no detalhe — está na lista de
                    pendências de apresentação. */}
                <span className={css.meta}>
                  {[i.uf, i.municipio].filter(Boolean).join(" / ") || "local não informado"}
                </span>
                <span className={css.objeto}>{i.objeto}</span>
                <span className={css.meta}>
                  {i.valor_estimado ?? "valor não informado"}
                  {i.encerramento ? ` · encerra ${dia(i.encerramento)}` : " · sem prazo"}
                  {i.modalidade ? ` · ${i.modalidade}` : ""}
                  {i.vista_em ? ` · encontrada ${dia(i.vista_em)}` : ""}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      <Link className={css.chamada} to="/oportunidades?ordem=recente">
        ver todas as oportunidades →
      </Link>
    </section>
  );
}

/** "3 de 7 (43%)" — a taxa com o tamanho da amostra à vista: com poucas
 * decisões a porcentagem sozinha engana. */
function deTantas(aprovadas: number, decididas: number) {
  if (!decididas) return "nenhuma decidida";
  return `${aprovadas} de ${decididas} (${Math.round((100 * aprovadas) / decididas)}%)`;
}

/** O PERFIL DAS APROVADAS (pedido do Pedro, 04/11: "quero que seja
 * diariamente"). Medido em produção no mesmo dia: das 90 do perfil que
 * chegaram a Avaliar em 49 dias, 8 venceram sem ninguém decidir, com 22 dias
 * de prazo na mediana. A lista é o perfil em Avaliar que espera decisão, com
 * as UFs da frente e o prazo mais curto primeiro; os números são a rodada da
 * noite, as que venceram e a taxa em janela móvel (por dia ela é ruído).
 * Calculado pelo servidor na hora (`GET /api/perfil`). Só mostra e ordena:
 * nada aqui muda score, aba ou fila. */
function PerfilDasAprovadas() {
  const q = useQuery({ queryKey: ["perfil-do-dia"], queryFn: api.perfil });

  if (q.isPending) return null;
  if (q.error)
    return (
      <div className={css.perfil}>
        <Aviso tom="erro" simples>
          O perfil das aprovadas não respondeu.{" "}
          {q.error instanceof Error ? q.error.message : String(q.error)}
        </Aviso>
      </div>
    );
  const p = q.data;
  const [j7, j30] = p.janelas;
  const r = p.rodada;
  const d = p.definicao;
  const motivos = (j: typeof j7) =>
    j ? `reprovadas: ${j.praca} pela praça, ${j.objeto} pelo objeto, ${j.outra} por outro motivo` : "";

  return (
    <div className={css.perfil}>
      <Secao
        titulo="O perfil das aprovadas"
        conta={p.esperando.length}
        nota={
          <>
            Oportunidades parecidas com as que a gestão aprovou: do escopo, de{" "}
            {[...d.tipos, ...d.categorias].join(", ").replaceAll("_", " ")}, e sem{" "}
            {d.tipos_recusados.join(", ").replaceAll("_", " ")}. A lista é a aba Avaliar sem
            decisão, com {d.ufs_na_frente.join(", ")} na frente e o prazo mais curto
            primeiro. Só mostra e ordena; a definição está em Configuração › Fila.
          </>
        }
      >
        <Cifras
          itens={[
            {
              rotulo: "do perfil na última rodada",
              valor: r.do_perfil,
              ajuda:
                `${r.novas} novas na rodada de ${dia(r.inicio).slice(0, 5)} para ` +
                `${dia(r.fim).slice(0, 5)}; ${r.na_frente} das UFs da frente; ` +
                `${r.em_avaliar} já em Avaliar`,
            },
            {
              rotulo: "esperando decisão",
              valor: p.esperando.length,
              ajuda: "do perfil, na aba Avaliar, sem decisão de ninguém",
            },
            {
              rotulo: `venceram sem decisão (${p.venceram_dias} dias)`,
              valor: p.venceram,
              ajuda:
                "do perfil, com score e valor de Avaliar, encerraram sem ninguém decidir: " +
                "é a perda que esta lista existe para evitar",
            },
            {
              rotulo: `aprovadas em ${j7?.dias ?? 7} dias`,
              valor: j7 ? deTantas(j7.aprovadas, j7.decididas) : "—",
              ajuda: motivos(j7),
            },
            {
              rotulo: `aprovadas em ${j30?.dias ?? 30} dias`,
              valor: j30 ? deTantas(j30.aprovadas, j30.decididas) : "—",
              ajuda: motivos(j30),
            },
          ]}
        />
        {p.esperando.length === 0 ? (
          <Vazio titulo="Nenhuma do perfil esperando decisão.">
            Quando chegar uma do perfil em Avaliar, ela aparece aqui antes de vencer.
          </Vazio>
        ) : (
          <Lista rotulo="Do perfil, esperando decisão">
            {p.esperando.map((e) => (
              <ItemDeLista
                key={e.licitacao_id}
                inicio={
                  <Pilula
                    tom={e.prazo_curto ? "perigo" : "neutro"}
                    title={
                      e.encerramento
                        ? `encerra em ${dia(e.encerramento)}` +
                          (e.prazo_curto
                            ? ` — menos de ${p.prazo_curto_dias} dias para articular parceiro`
                            : "")
                        : "sem prazo de encerramento"
                    }
                  >
                    {e.dias == null ? "sem prazo" : e.dias === 1 ? "1 dia" : `${e.dias} dias`}
                  </Pilula>
                }
                titulo={
                  <Link to={`/licitacao/${e.licitacao_id}`}>
                    {e.orgao ?? "Órgão não informado"}
                  </Link>
                }
                texto={e.objeto}
                meta={
                  <>
                    <span>
                      {[e.uf, e.municipio].filter(Boolean).join(" / ") || "local não informado"}
                    </span>
                    <span>{e.valor ?? "valor não informado"}</span>
                    {e.tipos.length > 0 && <span>{e.tipos.join(", ").replaceAll("_", " ")}</span>}
                  </>
                }
                fim={<b title="score">{e.score ?? "—"}</b>}
              />
            ))}
          </Lista>
        )}
      </Secao>
    </div>
  );
}

/** Cada tipo de achado, na ordem do servidor (`auditoria.TIPOS`): é a do
 * pedido, do que mais engana quem olha a aba ao que é da noite inteira. */
const TIPOS_DE_ACHADO: Record<TipoDeAchado, string> = {
  travada_em_69: "Travadas em 69 pela qualificação",
  nogo_em_avaliar: "NO-GO em Avaliar",
  duplicada: "A mesma oportunidade duas vezes",
  leitura_parada: "Leituras e cruzamentos que pararam",
  noite_curta: "A noite",
};

/** PARA CONFERIR DA ÚLTIMA NOITE (pedido do Pedro, 16/10): "Hoje quem descobre
 * o defeito é o chefe, olhando a aba." O que tem cara de erro, calculado pelo
 * servidor NA HORA em que a tela abre (`GET /api/auditoria`) — o que alguém
 * consertou sai sozinho. Sem achado, o bloco não aparece: um "tudo certo"
 * permanente viraria mobília. Com a auditoria fora do ar, a tela DIZ isso, em
 * vez de calar como se não houvesse nada. */
function ParaConferir() {
  const q = useQuery({ queryKey: ["auditoria"], queryFn: api.auditoria });

  if (q.isPending) return null;
  if (q.error)
    return (
      <p className={css.erro}>
        <b>A auditoria da última noite não respondeu.</b>{" "}
        {q.error instanceof Error ? q.error.message : String(q.error)}
      </p>
    );
  const { achados, inicio, fim } = q.data;
  if (!achados.length) return null;

  const grupos = (Object.keys(TIPOS_DE_ACHADO) as TipoDeAchado[])
    .map((tipo) => [tipo, achados.filter((a) => a.tipo === tipo)] as const)
    .filter(([, itens]) => itens.length > 0);

  return (
    <section className={css.conferir}>
      <h2 className={css.conferirTitulo}>
        Para conferir da última noite
        <span className={css.contagem}>{achados.length}</span>
      </h2>
      <p className={css.conferirNoite}>
        A noite de {dia(inicio).slice(0, 5)} para {dia(fim).slice(0, 5)}, e o estado das abas
        agora. Cada linha aponta; nenhuma muda aba, score ou fila.
      </p>
      {grupos.map(([tipo, itens]) => (
        <div key={tipo} className={css.conferirGrupo}>
          <h3>{TIPOS_DE_ACHADO[tipo]}</h3>
          <ul>
            {itens.map((a, i) => (
              <Achado key={`${a.licitacao_id ?? "noite"}-${i}`} a={a} />
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

function Achado({ a }: { a: AchadoDaAuditoria }) {
  return (
    <li>
      {a.licitacao_id != null ? (
        <Link to={`/licitacao/${a.licitacao_id}`}>
          <strong>{a.titulo}</strong>
        </Link>
      ) : (
        <strong>{a.titulo}</strong>
      )}
      <span className={css.achadoDetalhe}>{a.detalhe}</span>
    </li>
  );
}

const ESTADO: Record<string, { rotulo: string; classe: string; ajuda: string }> = {
  ok: { rotulo: "concluída", classe: "ok", ajuda: "Rodou e viu o que havia." },
  parcial: {
    rotulo: "parcial",
    classe: "atencao",
    ajuda:
      "Terminou, mas alguma etapa falhou — viu menos do que existia. Não é o " +
      "mesmo que ter falhado, e não é o mesmo que ter corrido bem.",
  },
  falha: {
    rotulo: "falhou",
    classe: "nao",
    ajuda: "Uma etapa essencial caiu e a execução parou. A fila do dia não foi produzida.",
  },
  cancelada: {
    rotulo: "cancelada",
    classe: "neutro",
    ajuda: "Alguém mandou parar. Não quebrou — foi interrompida.",
  },
  rodando: { rotulo: "em curso", classe: "ativo", ajuda: "Está rodando agora." },
  pulada: {
    rotulo: "pulada",
    classe: "neutro",
    ajuda:
      "Não rodou. Numa rodada seca, é uma etapa sem modo seco — executá-la " +
      "escreveria no banco. Não conta como sucesso.",
  },
};

function horas(h: number | null, nunca = "nunca rodou") {
  if (h === null) return nunca;
  if (h < 1) return `há ${Math.round(h * 60)} min`;
  if (h < 48) return `há ${h.toFixed(1).replace(".", ",")} h`;
  return `há ${Math.floor(h / 24)} dias`;
}

function Etapa({ e }: { e: EtapaDoJob }) {
  const s = ESTADO[e.estado] ?? ESTADO.neutro!;
  return (
    <li>
      <span className={`${css.ponto} ${css[s.classe] ?? ""}`} />
      <span className={css.nome}>{e.etapa}</span>
      <span className={css.estado} title={s.ajuda}>
        {s.rotulo}
        {e.codigo_saida ? ` (${e.codigo_saida})` : ""}
      </span>
      {/* A cauda do que a etapa imprimiu só aparece quando algo deu errado.
          Mostrar sempre viraria ruído e esconderia justamente o caso em que
          ela importa. */}
      {e.estado !== "ok" && e.resumo && (
        <pre className={css.saida}>{e.resumo.slice(-600)}</pre>
      )}
    </li>
  );
}

export function Hoje() {
  const cliente = useQueryClient();
  const q = useQuery({
    queryKey: ["diario"],
    queryFn: api.diario,
    refetchInterval: (c) =>
      c.state.data?.ultimo?.estado === "rodando" ? 5000 : false,
  });

  const rodar = useMutation({
    mutationFn: api.rodarDiario,
    onSuccess: () => cliente.invalidateQueries({ queryKey: ["diario"] }),
  });
  const parar = useMutation({
    mutationFn: api.pararDiario,
    onSuccess: () => cliente.invalidateQueries({ queryKey: ["diario"] }),
  });

  if (q.isPending) return <p className={css.espera}>Carregando…</p>;
  if (q.error) return <p className={css.espera}>{String(q.error)}</p>;

  const d = q.data;
  const rodando = d.ultimo?.estado === "rodando";
  const s = d.ultimo ? (ESTADO[d.ultimo.estado] ?? ESTADO.neutro!) : null;
  const erroRodar =
    rodar.error instanceof ErroApi ? rodar.error.message : null;

  return (
    <div className={css.tela}>
      {/* O QUE TEM CARA DE ERRO, no topo (16/10): é o que o chefe veria
          primeiro na aba, e a tela tem de mostrar antes dele. */}
      <ParaConferir />

      {/* O aviso vem ANTES de tudo, inclusive dos números. Um relatório bonito
          de uma execução que não aconteceu é pior que nenhum relatório. */}
      <div className={`${css.pulso} ${d.em_atraso ? css.atrasado : ""}`}>
        <div>
          <strong>{horas(d.horas_atras)}</strong>
          <span>
            {d.em_atraso
              ? `O job não roda há mais de ${d.alerta_horas} h. ` +
                (d.horas_atras === null
                  ? "Nenhuma execução foi registrada — provavelmente ninguém agendou."
                  : "Verifique o agendamento: nada aqui detecta um cron que parou.")
              : `Última execução${d.ultimo?.gatilho === "agendado" ? " agendada" : ""}` +
                `${d.ultimo?.seco ? ", em modo seco" : ""}.`}
          </span>
        </div>
        <div className={css.acoes}>
          {rodando ? (
            <button type="button" onClick={() => parar.mutate()}>
              parar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => rodar.mutate()}
              disabled={rodar.isPending}
            >
              {rodar.isPending ? "disparando…" : "rodar agora"}
            </button>
          )}
        </div>
      </div>

      {/* O SEGUNDO batimento. Mesmo desenho do primeiro e pelo mesmo motivo: o
          cron não avisa quando ele próprio para, e a única forma de descobrir
          que não há backup é precisar de um.

          Fica aqui em cima e não na lista do fluxo porque não é uma etapa — é
          a outra coisa que tem de acontecer sozinha todo dia. E um grau menor
          que o primeiro porque o job parado custa o dia; o backup parado custa
          no dia em que outra coisa der errado. */}
      <div
        className={`${css.pulso} ${css.segundo} ${
          d.backup.em_atraso ? css.atrasado : ""
        }`}
      >
        <div>
          <strong>backup {horas(d.backup.horas_atras, "nunca houve")}</strong>
          <span>
            {d.backup.horas_atras === null
              ? "Nenhum backup registrado. Um `down -v` já custou 339 licitações e 156 triagens."
              : d.backup.estado !== "ok"
                ? "O último saiu SUSPEITO: o arquivo existe, a conferência não fechou. Ver nectuno-backup --lista."
                : d.backup.em_atraso
                  ? `Passou de ${d.backup.alerta_horas} h desde o último. Nada aqui detecta um cron que parou.`
                  : d.backup.provado
                    ? "Restaurado num banco descartável e conferido linha por linha."
                    : "Arquivo conferido: dump válido e completo em tabelas — não restaurado."}
          </span>
        </div>
        {d.backup.tamanho !== null && (
          <span className={css.medida}>
            {(d.backup.tamanho / 1048576).toFixed(1).replace(".", ",")} MB
          </span>
        )}
      </div>

      {erroRodar && <p className={css.erro}>{erroRodar}</p>}

      {/* OS NÚMEROS SÃO DAS RECÉM-ENCONTRADAS (decisão do Pedro, 2026-09-22):
          "a tela Hoje deve mostrar as oportunidades mais recentes encontradas
          pelo sistema". Antes eram o acervo inteiro (1.938), as triadas e as
          pontuadas — números que contam até o que a Regra 1 esconde e não
          dizem nada sobre o dia. Estes contam dentro do funil e da Regra 1. */}
      <div className={css.numeros}>
        {(
          [
            [
              `encontradas em ${d.fila.janela_horas} h`,
              d.fila.novas,
              "vistas pela primeira vez na janela, já dentro do funil e da Regra 1",
            ],
            ["em Avaliar", d.fila.novas_avaliar, "das novas: score 70 ou mais com contrato de pelo menos R$ 250 mil, ou sem valor informado"],
            ["em Descartadas", d.fila.novas_descartadas, "das novas: score de 50 a 69, ou 70 ou mais abaixo de R$ 250 mil"],
            [
              "aguardando score",
              d.fila.novas_sem_score,
              "das novas: a IA ainda não as julgou, então ainda não têm aba",
            ],
            [
              "esperando você",
              d.fila.aguardando_decisao,
              "a aba Avaliar inteira, de qualquer data, ainda sem decisão humana",
            ],
            // AGUARDANDO LEITURA (D17, 2026-10-05): só há alguém aqui com o
            // portão ligado no servidor, e sem ninguém o número não é
            // desenhado — zero para sempre seria ruído.
            ...(d.fila.aguardando_leitura > 0
              ? [
                  [
                    "aguardando leitura",
                    d.fila.aguardando_leitura,
                    "teriam score para Avaliar, mas o edital ainda não foi lido inteiro e cruzado com os atestados",
                  ] as const,
                ]
              : []),
          ] as const
        ).map(([rotulo, valor, ajuda]) => (
          <div key={rotulo} className={css.numero} title={ajuda}>
            <b>{valor}</b>
            <span>{rotulo}</span>
            {/* A mesma ajuda por escrito, só no toque: lá o `title` não
                aparece, e a regra do documento por trás do número sumia. */}
            <small className={css.explica}>{ajuda}</small>
          </div>
        ))}
      </div>

      {/* O PERFIL DAS APROVADAS (04/11): o que fazer hoje, logo depois dos
          números do dia — a do perfil que vence sem decisão é a perda mais
          barata de evitar. */}
      <PerfilDasAprovadas />

      <Recentes />

      {/* O QUE O JOB NÃO ESTÁ OLHANDO. Vem depois dos números de propósito:
          eles dizem o que foi encontrado, isto diz o que ficou de fora — e
          sem este bloco alguém restringiria a coleta a três estados e o
          sistema seguiria parecendo saudável, com `ok` todo dia e a fila
          secando devagar. Mesmo risco do vocabulário, e é reversível só
          porque é parâmetro de consulta. */}
      {d.coleta.erro ? (
        <p className={css.erro}>
          <b>Configuração de coleta impossível.</b> {d.coleta.erro}
        </p>
      ) : d.coleta.recortes.length > 0 ? (
        <div className={css.recorte}>
          <strong>O job não varre tudo.</strong>
          <ul>
            {d.coleta.recortes.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
          <span>
            {d.coleta.varreduras} varredura{d.coleta.varreduras === 1 ? "" : "s"} por
            execução. Ninguém repara na ausência do que nunca esteve na tela — se o
            recorte não for intencional, ele está em Configuração › coleta.
          </span>
        </div>
      ) : (
        <p className={css.tudo}>
          O job varre tudo que as fontes expõem, em{" "}
          {d.coleta.varreduras} varredura
          {d.coleta.varreduras === 1 ? "" : "s"} — Licitmais pela pesquisa
          detalhada, PNCP sem recorte e o portal do Sebrae.
        </p>
      )}

      {d.fila.aguardando_decisao > 0 && (
        <Link className={css.chamada} to="/oportunidades?faixa=avaliar&decisao=pendente">
          ver as {d.fila.aguardando_decisao} que esperam decisão →
        </Link>
      )}

      {/* ── O BALANÇO ──────────────────────────────────────────────────
          Cada etapa aparece pronta porque existe DADO provando, não porque
          alguém digitou. Um quadro de status escrito à mão continuaria
          dizendo "score: pronto" depois de a tabela cair, e seria pior que
          não ter quadro — porque quem lesse acreditaria.

          E as pendências dizem de QUEM dependem: separar "falta fazer" de
          "falta decidir" é o que evita a pergunta "e isso depende de mim?". */}
      <h2 className={css.titulo}>O que está de pé</h2>
      <ul className={css.fluxo}>
        {d.balanco.etapas.map((e) => (
          <li key={e.numero} className={e.pronta ? "" : css.faltando}>
            <span className={`${css.ponto} ${e.pronta ? css.ok : css.neutro}`} />
            <span className={css.nome}>
              {e.numero} · {e.nome}
            </span>
            <span className={css.medida} title={e.onde}>
              {e.medida}
            </span>
          </li>
        ))}
      </ul>

      <h2 className={css.titulo}>
        O que falta
        <span className={css.contagem}>{d.balanco.pendencias.length}</span>
      </h2>
      {d.balanco.pendencias.length === 0 ? (
        <p className={css.tudo}>Nada pendente que o sistema saiba medir.</p>
      ) : (
        <ul className={css.pendencias}>
          {d.balanco.pendencias.map((p) => (
            <li key={p.o_que}>
              <span className={css.dequem}>{p.de_quem}</span>
              <span className={css.corpo}>
                <strong>{p.o_que}</strong>
                <span className={css.porque}>{p.porque}</span>
                {p.progresso && (
                  <span className={css.progresso}>{p.progresso}</span>
                )}
              </span>
              {/* O que o sistema não consegue medir fica dito. O leitor precisa
                  saber a diferença entre o que ele sabe e o que eu afirmei —
                  e as aferidas somem sozinhas quando resolvidas. */}
              {!p.aferida && (
                <span
                  className={css.naoaferida}
                  title="O sistema não tem como medir esta pendência: ela sai daqui quando alguém a tirar da lista, não sozinha."
                >
                  não aferível
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {d.ultimo && (
        <>
          <h2 className={css.titulo}>
            Última execução
            <span className={`${css.selo} ${css[s!.classe] ?? ""}`} title={s!.ajuda}>
              {s!.rotulo}
            </span>
          </h2>
          <ul className={css.etapas}>
            {d.etapas.map((e) => (
              <Etapa key={e.ordem} e={e} />
            ))}
          </ul>
          {d.ultimo.log_caminho && (
            <p className={css.log}>log completo: {d.ultimo.log_caminho}</p>
          )}
        </>
      )}
    </div>
  );
}
